import {z} from 'zod'

import {DisclaimerVariantEnum} from '@sdox/api'

import type {DynamicFieldData} from '../inputs'

export type BaseSchemaType = z.infer<typeof baseSchema>

const baseSchema = z.object({
  disclaimerId: z.string().optional().transform((v) => v === '' ? undefined : v),
  key: z.string().regex(/^[a-zA-Z0-9-]+$/),
})

export type InfoSchemaType = z.infer<typeof infoSchema>

const infoSchema = z.object({
  type: z.literal('XCInfoFootnoteType'),
  text: z.string(),
})

export type DocSchemaType = z.infer<typeof docSchema>

const docSchema = z.object({
  type: z.literal('XCDocDisclaimerType'),
  text: z.string(),
})

export type TagSchemaType = z.infer<typeof tagSchema>

const tagSchema = z.object({
  type: z.literal('XCRectangleFootnoteType'),
  variant: z.nativeEnum(DisclaimerVariantEnum),
  colorBackground: z.string().nullish(),
  colorBorder: z.string().nullish(),
  colorFont: z.string().nullish(),
  textContent: z.string().min(1),
  textHover: z.string().nullish(),
})

export type FormSchemaType = z.infer<typeof formSchema>

export const formSchema = z
  .discriminatedUnion('type', [
    infoSchema,
    docSchema,
    tagSchema,
  ])
  .and(baseSchema)

export const getFormInputFields = (type: FormSchemaType['type']): DynamicFieldData<any>[] => {
  const common: DynamicFieldData<BaseSchemaType & {type: string}>[] = [
    {
      fieldName: 'disclaimerId',
      label: '',
      inputType: 'hidden',
      required: true,
    },
    {
      fieldName: 'type',
      label: 'Type',
      inputType: 'select',
      options: [
        {
          label: 'Inline footnote',
          value: 'XCInfoFootnoteType',
        },
        {
          label: 'Tag',
          value: 'XCRectangleFootnoteType',
        },
        {
          label: 'Document disclaimer',
          value: 'XCDocDisclaimerType',
        },
      ],
      required: true,
    },
    {
      fieldName: 'key',
      label: 'Identifier',
      inputType: 'text',
      defaultValue: '',
      required: true,
    },
  ]

  const text: DynamicFieldData<DocSchemaType>[] = [
    {
      fieldName: 'text',
      label: 'Text',
      inputType: 'richtext',
      required: true,
    },
  ]

  const tag: DynamicFieldData<TagSchemaType>[] = [
    {
      fieldName: 'textHover',
      label: 'Text on hover',
      inputType: 'richtext',
      required: false,
    },
    {
      fieldName: 'variant',
      inputType: 'select',
      options: Object.entries(DisclaimerVariantEnum).map(([label, value]) => ({label, value})),
      defaultValue: DisclaimerVariantEnum.Primary,
      label: 'Variant',
      required: true,
    },
    {
      fieldName: 'textContent',
      inputType: 'text',
      placeholder: 'Text to show in the tag',
      label: 'Label',
      required: true,
    },
    {
      fieldName: 'colorBackground',
      inputType: 'color',
      placeholder: 'using variant color',
      label: 'Tag background color',
    },
    {
      fieldName: 'colorFont',
      inputType: 'color',
      placeholder: 'using variant color',
      label: 'Tag font color',
    },
    {
      fieldName: 'colorBorder',
      inputType: 'color',
      placeholder: 'using variant color',
      label: 'Tag border color',
    },
  ]

  return [
    ...common,
    ...(type === 'XCRectangleFootnoteType' ? tag : text),
  ]
}